import { Box, Button, Collapse, IconButton, Typography } from "@mui/material";
import clsx from "clsx";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import CloseIcon from "../../../../assets/images/icons/CloseIcon.tsx";
import WarningAlertIcon from "../../../../assets/images/icons/WarningAlertIcon.tsx";
import Vector from "../../../../assets/images/svgs/VectorIcon.svg";
import { engineExpansionError } from "../../../../constants/ImageToImageConstants.ts";
import LinkConstants from "../../../../constants/LinkConstants.ts";
import { useAppStore } from "../../../../hooks/useStores";
import { isAutomotiveApp } from "../../../../utils";
import styles from "./ImageToImageError.module.scss";

type Props = {
	className?: string;
	autoHideTimeout?: number; // Time in milliseconds to auto hide the error
};

const ImageToImageError = ({ className, autoHideTimeout = 10000 }: Props) => {
	const { t } = useTranslation("translation", { keyPrefix: "playground.imageToImage.errors" });
	const { imageToImageStore } = useAppStore();
	const [open, setOpened] = useState(false);
	const [message, setMessage] = useState("");
	const [boldMessage, setBoldMessage] = useState("");
	const [hideLearnMore, setHideLearnMore] = useState(false);
	const [errorStyle, setErrorStyle] = useState<"error" | "warning">("error");
	const [showErrorDissmisButton, setShowErrorDissmisButton] = useState(false);
	const [isSingleLineErrorMessage, setIsSingleLineErrorMessage] = useState(false);

	useEffect(() => {
		switch (imageToImageStore.errors?.generate?.status) {
			case 422:
				setOpened(true);
				if (imageToImageStore.errors?.generate?.message === engineExpansionError) {
					setHideLearnMore(true);
					setBoldMessage(t("expansionError"));
					setErrorStyle("warning");
					setMessage("");
				} else {
					setHideLearnMore(false);
					setBoldMessage(t("contentModerationErrorBoldMessage"));
					setMessage(t("contentModerationErrorMessage"));
					setErrorStyle("warning");
				}
				break;

			case 460:
				setOpened(true);
				setHideLearnMore(true);
				setErrorStyle("warning");
				setBoldMessage(t("errorOccuredBoldMessage"));
				setMessage(t("errorOccuredMessage"));
				setIsSingleLineErrorMessage(false);
				break;

			case 415:
				setOpened(true);
				setHideLearnMore(true);
				setErrorStyle("error");
				setMessage(t("unsupportedFileProvidedMessage"));
				setShowErrorDissmisButton(true);
				setIsSingleLineErrorMessage(true);
				setTimeout(() => (imageToImageStore.errors = {}), autoHideTimeout);
				break;

			default:
				setOpened(false);
				setBoldMessage("");
				setMessage("");
		}
	}, [imageToImageStore.errors.generate]);

	return (
		<Collapse in={open} onExited={() => (imageToImageStore.errors = {})}>
			{errorStyle == "error" ? (
				<Box
					className={clsx(className || "", {
						[styles.errorMessageContainer]: !isSingleLineErrorMessage,
						[styles.singleLineErrorContainer]: isSingleLineErrorMessage,
					})}
				>
					<img src={Vector} />
					<Typography className={styles.errorMessage}>{message}</Typography>

					{showErrorDissmisButton && (
						<IconButton
							className={styles.errorDismissButton}
							onClick={() => {
								imageToImageStore.errors = {};
								setShowErrorDissmisButton(false);
							}}
						>
							<CloseIcon />
						</IconButton>
					)}
				</Box>
			) : (
				<Box className={styles.warningMessageContainer}>
					<Box className={styles.warningTextContainer}>
						<WarningAlertIcon />
						<Box>
							<Typography className={styles.warningBoldMessage}>{boldMessage}</Typography>
							<Typography className={styles.warningMessage}>{message}</Typography>
						</Box>
					</Box>
					<Box className={styles.errorButtons}>
						{!hideLearnMore && (
							<Button
								component={"a"}
								className={styles.errorLearnMoreButton}
								href={LinkConstants.CONTENT_MODERATION_BRIA_API_DOC}
								target={"_blank"}
								rel={"noopener noreferrer"}
							>
								{t("learnMoreButton")}
							</Button>
						)}
						<Button
							className={clsx(styles.warningDismissButton, {
								[styles.warningAutomotiveDismissButton]: isAutomotiveApp(),
							})}
							onClick={() => (imageToImageStore.errors = {})}
						>
							{t("dismissButton")}
						</Button>
					</Box>
				</Box>
			)}
		</Collapse>
	);
};

export default observer(ImageToImageError);
